import { MultiValue, SingleValue, useChakraSelectProps } from "chakra-react-select";
import { useState, useMemo } from "react";
import { useController } from "react-hook-form";
import { ListQueryRequestType } from "shared/api/types/typedQueries";
import { useDebounce } from "shared/hooks/useDebounce";
import { FieldProps, OptionKeys, OptionV } from "shared/types/filters";
import useIsInvalid from "./useIsInvalid";

type OptionValue<O extends ListQueryRequestType> = {
  value: OptionV<O>[OptionKeys<O>],
  label: OptionV<O>[OptionKeys<O>],
}

const handleSelectMultiValue = <O extends ListQueryRequestType>(
  options: MultiValue<OptionValue<O>>,
  callback: (val: OptionValue<O>['value'][]) => void,
) => {
  const optionValues = options.map((option) => option.value);
  callback(optionValues);
};

const handleSelectSingleValue = <O extends ListQueryRequestType>(
  option: SingleValue<OptionValue<O>>,
  callback: (val: OptionValue<O>['value']) => void,
) => {
  callback(option.value);
};

const getValueByPath = (obj: UnknownRecord, path: string) => (
  path.split('.').reduce<unknown>((acc: unknown, key: string) => {
    if (acc && typeof acc === 'object' && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj)
);

const useSelectField = <O extends ListQueryRequestType>({
  name,
  optionsQuery,
  renderOptionLabel,
  labelName: propsLabelName,
  valueName: propsValueName,
  size,
  placeholder,
  isMulti,
  onChange: onCustomChange,
}: Omit<FieldProps<'async-select', 'input', O>, 'label'>) => {
  const {
    field: { onChange, value, ...fieldProps },
  } = useController({ name });
  const isInvalid = useIsInvalid(name);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce<string>(search, 500);
  const [wasOpened, setWasOpened] = useState(false);

  const { data, isFetching } = optionsQuery(
    {
      params: { search: debouncedSearch },
      pathParams: undefined
    },
    { enabled: wasOpened }
  );

  const labelName = propsLabelName || ('label' as keyof O);
  const valueName = propsValueName || ('value' as keyof O);

  const optionsObject = useMemo(
    () => data?.pages.flatMap((page) => page.data),
    [data, labelName, valueName]
  );

  const optionsToRender = useMemo(
    () =>
      data?.pages
        .flatMap((page) => page.data)
        .map((item) => ({
          label: renderOptionLabel ? renderOptionLabel(item) : getValueByPath(item as FixMeLater, labelName as string),
          value: getValueByPath(item as FixMeLater, valueName as string),
        }) as OptionValue<O>) ?? [],
    [data, labelName, valueName]
  );

  type MulOrSin<T extends boolean, Option = O> = T extends true ? MultiValue<OptionValue<Option>> : SingleValue<OptionValue<Option>>;
  const handleSelectValue = (value: MulOrSin<typeof isMulti>) => {
    const callback = (val: MulOrSin<typeof isMulti, OptionValue<O>['value']>) => {
      const objectValue = optionsObject.find(item => Array.isArray(val)
        ? val.includes(getValueByPath(item, valueName as string))
        : getValueByPath(item, valueName as string) === val) // TODO: I added this because the "val" can be an array of values
      onChange(val)
      if (onCustomChange) {
        onCustomChange(objectValue);
      }
      setSearch('');
    }

    if (isMulti) {
      handleSelectMultiValue<O>(value as MulOrSin<true>, callback as FixMeLater)
    } else {
      handleSelectSingleValue<O>(value as MulOrSin<false>, callback as FixMeLater)
    }
  };

  const selectProps = useChakraSelectProps<OptionValue<O>, typeof isMulti>({
    ...fieldProps,
    isMulti,
    value: isMulti
      ? optionsToRender.filter((option) => value.includes(option.value))
      : optionsToRender.find((option) => value === option.value),
    onChange: handleSelectValue,
    isInvalid,
    placeholder,
    isLoading: isFetching,
    options: optionsToRender,
    size,
  });

  const onMenuOpen = () => () => setWasOpened(true);
  const onMenuClose = () => () => setWasOpened(false);
  const onInputChange = (value: string) => setSearch(value);

  return {
    onMenuOpen,
    onMenuClose,
    onInputChange,
    ...selectProps,
  }
}

export default useSelectField;
